import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FrontechLogo from './FrontechLogo';
import { Eye, EyeOff, ArrowRight, Sun, Moon, CheckCircle2, Zap, Mail } from 'lucide-react';

const PERKS = [
  'Free forever plan — no credit card needed',
  'Your first agent live in under 5 minutes',
  'Full voice + text conversations included',
  'Analytics and conversation history',
];

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const { signup } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const { hasSession } = await signup(email, password, name);
      if (hasSession) {
        navigate('/dashboard');
      } else {
        // Supabase email confirmation is enabled — user must verify before logging in
        setCheckEmail(true);
      }
    } catch (err) {
      setError(err.message || 'Signup failed');
    }
    setLoading(false);
  };

  // ── Email confirmation screen ──
  if (checkEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--color-base)' }}>
        <div className="w-full max-w-sm text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'var(--color-accent-dim)', border: '1px solid rgba(0,214,126,0.2)' }}>
              <Mail size={28} style={{ color: 'var(--color-accent)' }} />
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>
            Check your email
          </h1>
          <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            We sent a confirmation link to
          </p>
          <p className="text-sm font-semibold mb-6" style={{ color: 'var(--color-accent)' }}>{email}</p>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            Click the link in your email to activate your account, then come back to sign in.
          </p>
          <Link to="/login" className="btn-accent w-full justify-center py-3 text-sm">
            Go to Sign In
          </Link>
          <p className="text-xs mt-4" style={{ color: 'var(--color-text-muted)' }}>
            Can't find it? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-base)' }}>

      {/* ── Left brand panel ── */}
      <div className="hidden lg:flex lg:w-[46%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'var(--color-surface)', borderRight: '1px solid var(--color-border)' }}>

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ opacity: 'var(--watermark-opacity)' }}>
          <FrontechLogo size={500} variant="watermark" color={`rgb(var(--watermark-color))`} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(91,127,166,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        {/* Top */}
        <div className="relative z-10 flex items-center gap-2.5">
          <FrontechLogo size={28} variant="icon" color="var(--color-accent)" />
          <span className="font-heading font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>Front</span>
          <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
        </div>

        {/* Center */}
        <div className="relative z-10">
          <FrontechLogo size={80} variant="animated" color="var(--color-accent)" className="mb-8" />
          <div className="flex items-center gap-2 mb-6 px-3 py-2 rounded-full w-fit"
            style={{ background: 'var(--color-accent-dim)', border: '1px solid rgba(0,214,126,0.15)' }}>
            <Zap size={13} style={{ color: 'var(--color-accent)' }} />
            <span className="text-xs font-semibold font-mono tracking-wide" style={{ color: 'var(--color-accent)' }}>
              Free to start
            </span>
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight leading-tight mb-4"
            style={{ color: 'var(--color-text-primary)' }}>
            Your business,<br />
            <span className="text-gradient-accent">now with a voice</span>
          </h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            Join businesses using Front to engage every website visitor with intelligent, personalized conversations.
          </p>
          <div className="space-y-3.5">
            {PERKS.map((perk, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 size={15} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{perk}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Trusted by 83+ businesses worldwide
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Front</span>
          </Link>
          <div className="lg:hidden flex-1" />
          <button
            onClick={toggle}
            className="p-2 rounded-lg cursor-pointer transition-colors ml-auto"
            style={{ color: 'var(--color-text-secondary)', background: 'var(--color-elevated)' }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 data-testid="signup-heading" className="font-heading text-2xl font-bold tracking-tight"
                style={{ color: 'var(--color-text-primary)' }}>
                Create your account
              </h1>
              <p className="mt-1.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Start building voice agents — it's free
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
              {error && (
                <div data-testid="signup-error"
                  className="text-sm rounded-xl px-4 py-3"
                  style={{
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171',
                  }}>
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Full Name
                </label>
                <input
                  data-testid="signup-name-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="Jane Smith"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Email
                </label>
                <input
                  data-testid="signup-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field focus-ring-accent"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    data-testid="signup-password-input"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input-field focus-ring-accent"
                    style={{ paddingRight: '44px' }}
                    placeholder="Min 6 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <button
                data-testid="signup-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-accent w-full justify-center py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ marginTop: '6px' }}
              >
                {loading
                  ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Creating account...</>
                  : <>Create Free Account <ArrowRight size={16} /></>
                }
              </button>
            </form>

            <p className="text-center text-sm mt-6" style={{ color: 'var(--color-text-muted)' }}>
              Already have an account?{' '}
              <Link to="/login" data-testid="signup-to-login-link"
                className="font-semibold transition-colors cursor-pointer"
                style={{ color: 'var(--color-accent)' }}>
                Sign in
              </Link>
            </p>

            <p className="text-center text-xs mt-4 leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
              By creating an account you agree to our{' '}
              <Link to="/terms" className="underline hover:opacity-80" style={{ color: 'var(--color-text-secondary)' }}>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline hover:opacity-80" style={{ color: 'var(--color-text-secondary)' }}>
                Privacy Policy
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
